import { retailCodeGtin14 } from './retailCode';
import type { PickVerifyUnitInput } from './retailCode';

/**
 * Linking a scanned UPC to the product the picker expected (`Product.upcs` on the server) — offered
 * when an OTC pack's retail UPC is not its NDC barcode and names nothing yet.
 *
 * The server refuses such a scan with `UPC_NOT_RECOGNIZED_MESSAGE` and `extensions.reason`
 * `UPC_NOT_RECOGNIZED` (libs/services pick-verify/product-code.ts; keep the two in step). The offer
 * is keyed on the reason, never the wording.
 */
export const UPC_NOT_RECOGNIZED_REASON = 'UPC_NOT_RECOGNIZED';

type GraphQLErrorLike = { extensions?: { reason?: string | null } | null };

/** The `extensions.reason` of a GraphQL refusal, or null. */
export const refusalReason = (error: unknown): string | null => {
  const list = (error as { graphQLErrors?: unknown } | null | undefined)?.graphQLErrors;
  const errors = Array.isArray(list) ? (list as GraphQLErrorLike[]) : [];
  const reason = errors.find((e) => typeof e?.extensions?.reason === 'string')?.extensions?.reason;
  return reason ?? null;
};

/**
 * The 12-digit UPC-A a scanned / typed code is, or null: a UPC-A, or its EAN-13 / GTIN-14 form
 * (leading zeros), with a valid check digit — the only codes the server will link.
 */
export const linkableUpc = (code: string | null | undefined): string | null => {
  const gtin14 = retailCodeGtin14(code);
  return gtin14 && gtin14.startsWith('00') ? gtin14.slice(2) : null;
};

/** What the link offer names: the UPC and the product it would be linked to. */
export type UpcLinkOffer = { upc: string; productId: string; productName: string; ndc: string | null };

/**
 * The offer for a refused unit, or null: only for an UPC_NOT_RECOGNIZED refusal, a code that is a
 * linkable UPC, an OTC NDC line (UPCs are linked to OTC products only), and a user who may update
 * products.
 */
export const upcLinkOffer = (
  reason: string | null | undefined,
  unit: PickVerifyUnitInput | null | undefined,
  line: {
    productName?: string | null;
    product?: { id?: string | null; fdaProduct?: { elevenDigitNdc?: string | null; deaSchedule?: string | null } | null } | null;
  },
  canLink: boolean
): UpcLinkOffer | null => {
  if (!canLink || reason !== UPC_NOT_RECOGNIZED_REASON) return null;
  const upc = linkableUpc(unit?.code);
  const productId = line.product?.id;
  const fda = line.product?.fdaProduct;
  if (!upc || !productId || (fda?.deaSchedule ?? '').trim() !== 'OTC') return null;
  return { upc, productId, productName: line.productName || 'this product', ndc: fda?.elevenDigitNdc ?? null };
};
